import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import React, { createContext, useContext } from "react";
import { useSelector } from "react-redux";

import { RootState } from "@/store/root-reducer";
import { ContactPageContent } from "@/utils/schema/contact-page-schema.utils";

import { useContactForm } from "../hooks/contact-form.hook";
import { formCmsData } from "../utils/contact-details-page.util";

type ContactMethod = "email" | "sms" | "telephone" | "post";
type ValidatorFunction = (value: string) => boolean;
type SetErrorFunction = React.Dispatch<React.SetStateAction<string>>;

const ContactDetailsContext = createContext<{
  cmsData: ContactPageContent;
  isFilled: boolean;
  flags: {
    continueIsLoading: boolean;
    editPage: string | null;
    isOpenUnsavedChangesModal: boolean;
    isPreferenceClicked: boolean;
    isTablet: [boolean, boolean];
    modalOpen: boolean;
    isLoading: boolean;
  };
  formErrors: {
    errorEmailMessage: string;
    invalidPhoneMessage: string;
  };
  formValues: {
    contactPreferenceLabels: (string | undefined)[];
    contactPreferences: ContactMethod[];
    dropDownListToDisplay: {
      value: string;
      code: string;
      optDropdownName: string;
      optDropdownTelCode: string;
      optDropdownFlag: {
        src: unknown;
        alt: string;
        width: number;
        height: number;
      };
    }[];
    email: string;
    phone: string;
    preferences: {
      [key: string]: boolean;
    };
    telCountryCode: string;
  };
  handlers: {
    isHideFooterBack: boolean;
    handleBack: () => void;
    handleBlur: (
      validator: ValidatorFunction,
      value: string,
      setError: SetErrorFunction
    ) => void;
    handleCancelEdit: () => void;
    handleContinue: () => Promise<void>;
    handleEmailChange: (address: string) => void;
    handleMobileNumberChange: (mobile: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleOptionSelect: (option: any) => void;
    handleUnSavedWarningMessageContinueBtn: () => void;
  };
  navigation: {
    router: AppRouterInstance;
  };
  refs: {
    emailRef: React.MutableRefObject<HTMLButtonElement | null>;
    phoneRef: React.MutableRefObject<HTMLButtonElement | null>;
  };
  setters: {
    setErrorEmailMessage: React.Dispatch<React.SetStateAction<string>>;
    setInvalidPhoneMessage: React.Dispatch<React.SetStateAction<string>>;
    setIsOpenUnsavedChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setPreferences: React.Dispatch<
      React.SetStateAction<{
        [x: string]: boolean;
      }>
    >;
  };
  validations: {
    isCtaActiveWhenTyping: boolean;
    isEmailValid: (value: string) => boolean;
    isEmpty: boolean;
    isPhoneNumberValid: (value: string) => boolean;
    isTyping: boolean;
  };
} | null>(null);

function ContactDetailsProvider({
  children,
  initialValue,
}: Readonly<{
  children: React.ReactNode;
  initialValue: ContactPageContent;
}>) {
  const { isFilled } = useSelector((state: RootState) => state.contactDetails);

  const {
    emailLabelIsEmpty,
    emailLabelIsInvalid,
    mobileLabelIsEmpty,
    mobileLabelIsInvalid,
  } = formCmsData(initialValue);

  const {
    flags,
    formErrors,
    formValues,
    handlers,
    navigation,
    refs,
    setters,
    validations,
  } = useContactForm({
    emailLabelIsEmpty,
    emailLabelIsInvalid,
    contactFormContent: initialValue.contactDetailsArea[0].leftArea[0],
    mobileLabelIsEmpty,
    mobileLabelIsInvalid,
  });

  const contextValue = React.useMemo(
    () => ({
      cmsData: initialValue,
      isFilled,
      flags,
      formErrors,
      formValues,
      handlers,
      navigation,
      refs,
      setters,
      validations,
    }),

    [
      initialValue,
      isFilled,

      flags,
      formErrors,
      formValues,
      handlers,
      navigation,
      refs,
      setters,
      validations,
    ]
  );

  return (
    <ContactDetailsContext.Provider value={contextValue}>
      {children}
    </ContactDetailsContext.Provider>
  );
}

export const useContactDetailsContext = () => {
  const context = useContext(ContactDetailsContext);
  if (!context) {
    throw new Error(
      "useContactDetailsContext must be used within a ContactDetailsMagnoliaProvider"
    );
  }
  return context;
};

export default ContactDetailsProvider;
